/**
 * ResidentHistory — S3. Chronological log of everything tied to the unit:
 * pickups, QR scans, violations, notifications. Filter by category + dates.
 */
import { useCallback, useEffect, useState } from 'react';
import { History, Trash2, AlertTriangle, Bell } from 'lucide-react';
import { apiFetch } from '../../lib/apiFetch';

const GOLD = '#C9A962';
const CATS = [['all', 'All'], ['pickups', 'Pickups'], ['violations', 'Violations'], ['notifications', 'Notifications']];
const TYPE_META = {
  pickup: { Icon: Trash2, color: '#60a5fa', label: 'Pickup' },
  violation: { Icon: AlertTriangle, color: '#f59e0b', label: 'Violation' },
  notification: { Icon: Bell, color: '#C9A962', label: 'Notification' },
};
const STATUS_CLS = {
  requested: 'bg-blue-950/50 text-blue-400', submitted: 'bg-blue-950/50 text-blue-400',
  reviewed: 'bg-amber-950/50 text-amber-400', in_progress: 'bg-purple-950/50 text-purple-400',
  resolved: 'bg-emerald-950/50 text-emerald-400', read: 'bg-zinc-800 text-zinc-500',
  unread: 'bg-red-950/50 text-red-400',
};

export function HistoryList({ entries }) {
  if (!entries.length) {
    return <p className="text-xs text-zinc-600 rounded-xl border border-zinc-800 p-6 text-center">No activity in this range.</p>;
  }
  return (
    <div className="space-y-2">
      {entries.map((e, i) => {
        const meta = TYPE_META[e.type] || TYPE_META.notification;
        return (
          <div key={i} className="rounded-xl border border-zinc-800 bg-zinc-900/60 p-3.5" data-testid={`history-entry-${i}`} data-type={e.type}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2.5 min-w-0">
                <meta.Icon className="h-4 w-4 mt-0.5 shrink-0" style={{ color: meta.color }} />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-white leading-tight">{e.title}
                    {e.reference && <span className="ml-1.5 font-mono text-[10px]" style={{ color: GOLD }}>{e.reference}</span>}
                  </p>
                  {e.detail && <p className="text-[11px] text-zinc-400 mt-0.5">{e.detail}</p>}
                  {e.notes && <p className="text-[11px] text-emerald-400 mt-0.5">Staff notes: {e.notes}</p>}
                  {e.resident_name && <p className="text-[10px] text-zinc-600 mt-0.5">{e.resident_name}{e.unit_number ? ` · unit ${e.unit_number}` : ''}</p>}
                </div>
              </div>
              <div className="text-right shrink-0">
                {e.status && (
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase ${STATUS_CLS[e.status] || 'bg-zinc-800 text-zinc-400'}`}>
                    {String(e.status).replace('_', ' ')}
                  </span>
                )}
                <p className="text-[10px] text-zinc-600 mt-1">{new Date(e.date).toLocaleString()}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function HistoryFilters({ cat, setCat, from, setFrom, to, setTo, testPrefix = 'history' }) {
  const inputCls = 'rounded bg-zinc-900 border border-zinc-700 px-2 py-1.5 text-white text-xs';
  return (
    <div className="space-y-2 mb-3">
      <div className="flex gap-1.5 flex-wrap">
        {CATS.map(([k, l]) => (
          <button key={k} onClick={() => setCat(k)}
            className="px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border"
            style={cat === k ? { backgroundColor: GOLD, color: '#1a1a1a', borderColor: GOLD } : { color: '#a1a1aa', borderColor: '#3f3f46' }}
            data-testid={`${testPrefix}-cat-${k}`}>
            {l}
          </button>
        ))}
      </div>
      <div className="flex gap-2">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inputCls} data-testid={`${testPrefix}-from`} />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inputCls} data-testid={`${testPrefix}-to`} />
      </div>
    </div>
  );
}

export default function ResidentHistory() {
  const [entries, setEntries] = useState(null);
  const [cat, setCat] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async () => {
    const qs = new URLSearchParams({ category: cat, ...(from && { date_from: from }), ...(to && { date_to: to }) });
    try {
      const d = await apiFetch(`/api/resident/history?${qs}`);
      setEntries(d.entries || []);
    } catch (_) { setEntries([]); }
  }, [cat, from, to]);

  useEffect(() => { load(); }, [load]);

  if (entries === null) return null;

  return (
    <div data-testid="resident-history-screen">
      <h2 className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.25em] mb-3" style={{ color: GOLD }}>
        <History className="h-4 w-4" /> Service History
      </h2>
      <HistoryFilters cat={cat} setCat={setCat} from={from} setFrom={setFrom} to={to} setTo={setTo} />
      <HistoryList entries={entries} />
    </div>
  );
}
